import React, { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ChevronsUpDown, Loader2, AlertTriangle, PlusCircle, Car } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { VehicleListItem } from '@/app/types/vehicle';
import { listVehicles, getVehicle } from '@/app/services/vehicleService';
import { debounce } from 'lodash';
import { PaginatedResponse } from '@/app/types/api';
import QuickVehicleForm from './QuickVehicleForm';

interface VehicleSelectorProps {
  value: number | undefined;
  onChange: (value: number | undefined, vehicle?: VehicleListItem) => void;
  placeholder?: string;
  ownerId?: number; // Filter vehicles by owner
  disabled?: boolean;
  onCreateNew?: () => void; // Optional callback for "Create New" action
}

export const VehicleSelector: React.FC<VehicleSelectorProps> = ({
  value,
  onChange,
  placeholder = 'Select vehicle...',
  ownerId,
  disabled = false,
  onCreateNew,
}) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isQuickFormOpen, setIsQuickFormOpen] = useState(false);
  const queryClient = useQueryClient();

  const debouncedSetSearchQuery = useMemo(() => debounce(setSearchQuery, 300), []);

  const vehiclesQuery = useQuery<PaginatedResponse<VehicleListItem>, Error>({
    queryKey: ['vehicles', { search: searchQuery, owner_id: ownerId, limit: 100 }],
    queryFn: () =>
      listVehicles({
        search_query: searchQuery || undefined,
        owner_id: ownerId,
        limit: 100,
        sort_by: 'registration_number',
        sort_dir: 'asc',
      }),
    enabled: open || !!value,
    staleTime: 5 * 60 * 1000,
  });

  // Separate query to fetch the selected vehicle if not in the list
  const selectedVehicleQuery = useQuery({
    queryKey: ['vehicle', value],
    queryFn: () => getVehicle(value!),
    enabled: !!value && !vehiclesQuery.data?.items.find((v) => v.id === value),
    staleTime: 5 * 60 * 1000,
  });

  const vehicles = vehiclesQuery.data?.items ?? [];
  const selectedVehicle = useMemo(() => {
    // First try to find in the vehicles list
    const vehicleFromList = vehicles.find((v) => v.id === value);
    if (vehicleFromList) return vehicleFromList;

    // If not found in list, use the individually fetched vehicle
    if (selectedVehicleQuery.data) {
      return {
        id: selectedVehicleQuery.data.id,
        registration_number: selectedVehicleQuery.data.registration_number,
        make: selectedVehicleQuery.data.make,
        model: selectedVehicleQuery.data.model,
        variant: selectedVehicleQuery.data.variant,
        year_of_manufacture: selectedVehicleQuery.data.year_of_manufacture,
        color: selectedVehicleQuery.data.color,
        fuel_type: selectedVehicleQuery.data.fuel_type,
        transmission: selectedVehicleQuery.data.transmission,
        owner: selectedVehicleQuery.data.owner
          ? {
              id: selectedVehicleQuery.data.owner.id,
              name: selectedVehicleQuery.data.owner.name,
              phone: selectedVehicleQuery.data.owner.phone,
            }
          : null,
        last_service_date: selectedVehicleQuery.data.last_service_date,
        last_service_odometer: selectedVehicleQuery.data.last_service_odometer,
        created_at: selectedVehicleQuery.data.created_at,
      } as VehicleListItem;
    }
    return undefined;
  }, [vehicles, value, selectedVehicleQuery.data]);

  const handleCreateNew = (): void => {
    setOpen(false);
    if (onCreateNew) {
      onCreateNew();
    } else {
      setIsQuickFormOpen(true);
    }
  };

  const handleQuickVehicleCreated = (vehicleId: number, vehicle: any): void => {
    const vehicleListItem: VehicleListItem = {
      id: vehicle.id,
      registration_number: vehicle.registration_number,
      make: vehicle.make,
      model: vehicle.model,
      variant: vehicle.variant,
      year_of_manufacture: vehicle.year_of_manufacture,
      color: vehicle.color,
      fuel_type: vehicle.fuel_type,
      transmission: vehicle.transmission,
      owner: vehicle.owner ? { id: vehicle.owner.id, name: vehicle.owner.name, phone: vehicle.owner.phone } : null,
      last_service_date: vehicle.last_service_date,
      last_service_odometer: vehicle.last_service_odometer,
      created_at: vehicle.created_at,
    };
    onChange(vehicleId, vehicleListItem);
    queryClient.invalidateQueries({ queryKey: ['vehicles'] });
  };

  // Format vehicle display text
  const formatVehicleDisplay = (vehicle: VehicleListItem): string => {
    const parts = [vehicle.registration_number];
    if (vehicle.make || vehicle.model) {
      parts.push(`${vehicle.make || ''} ${vehicle.model || ''}`.trim());
    }
    return parts.join(' | ');
  };

  // Ensure the selected vehicle is in the dropdown list
  const displayVehicles = useMemo(() => {
    if (!selectedVehicle || vehicles.find((v) => v.id === selectedVehicle.id)) {
      return vehicles;
    }
    // Add the selected vehicle to the beginning of the list if it's not there
    return [selectedVehicle, ...vehicles];
  }, [vehicles, selectedVehicle]);

  return (
    <>
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn('w-full justify-between', !value && 'text-muted-foreground')}
          disabled={disabled}
        >
          <div className="flex items-center gap-2 truncate">
            <Car className="h-4 w-4 shrink-0" />
            {selectedVehicle
              ? formatVehicleDisplay(selectedVehicle)
              : value && (vehiclesQuery.isLoading || selectedVehicleQuery.isLoading)
                ? 'Loading...'
                : placeholder}
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] max-h-[--radix-popover-content-available-height] p-0">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search by registration, make, model..."
            onValueChange={debouncedSetSearchQuery}
            disabled={disabled}
          />
          <CommandList>
            {vehiclesQuery.isLoading && (
              <div className="p-2 flex items-center justify-center">
                <Loader2 className="h-4 w-4 animate-spin" />
              </div>
            )}
            {vehiclesQuery.isError && (
              <div className="p-2 text-center text-sm text-destructive flex items-center justify-center">
                <AlertTriangle className="h-4 w-4 mr-2" /> Error loading vehicles
              </div>
            )}
            {!vehiclesQuery.isLoading &&
              !vehiclesQuery.isError &&
              vehicles.length === 0 &&
              !searchQuery && <CommandEmpty>No vehicles found.</CommandEmpty>}
            {!vehiclesQuery.isLoading &&
              !vehiclesQuery.isError &&
              vehicles.length === 0 &&
              searchQuery && (
                <CommandEmpty>No vehicles match &quot;{searchQuery}&quot;.</CommandEmpty>
              )}

            {!vehiclesQuery.isLoading && !vehiclesQuery.isError && (
              <ScrollArea className="max-h-52">
                <CommandGroup>
                  {displayVehicles.map((vehicle) => (
                    <CommandItem
                      key={vehicle.id}
                      value={vehicle.registration_number}
                      onSelect={() => {
                        onChange(vehicle.id, vehicle);
                        setOpen(false);
                        setSearchQuery('');
                      }}
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          value === vehicle.id ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                      <div className="flex flex-col">
                        <span className="font-medium">{vehicle.registration_number}</span>
                        <span className="text-xs text-muted-foreground">
                          {vehicle.make} {vehicle.model}
                          {vehicle.owner && ` | ${vehicle.owner.name}`}
                        </span>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </ScrollArea>
            )}
            <CommandSeparator />
            <CommandItem onSelect={handleCreateNew} className="cursor-pointer group">
              <PlusCircle className="mr-2 h-4 w-4 text-primary group-hover:text-primary-foreground" />
              Register New Vehicle
            </CommandItem>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
    <QuickVehicleForm
      open={isQuickFormOpen}
      onOpenChange={setIsQuickFormOpen}
      onCreated={handleQuickVehicleCreated}
    />
    </>
  );
};
